import { useState, useEffect, useContext, useRef, useMemo } from "react";
import {
  Layout,
  Row,
  Col,
  Form,
  Select,
  Button,
  DatePicker,
  Modal,
} from "antd";
import dayjs from "dayjs";
import DateUtils from "../../../utils/date";
import { DownloadOutlined, CloseCircleFilled } from "@ant-design/icons";
import {
  GetCustomerList,
  GetCustomerUploadMasterList,
  GetProductStatusCount,
} from "../../../utils/api.constant";
import { eResultCode } from "../../../utils/enum";
import useFetch from "../../../hooks/useFetch";
import useToast from "../../../hooks/useToast";
import Utils from "../../../utils";
import { ToastOpen, ToastType } from "../../../state/toast/slice";
import StackedBarChart from "./chart";
import styles from "./dashboard.module.css";
import { TContext, TDropdownOptions } from "../../../types/config";
import { UserContext } from "../../../context";
import { TFileManagerMaster } from "../../../types";
import { RecentUploadsList } from "./file-management-list";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import html2canvas from "html2canvas";

function DashboardView() {
  const { post } = useFetch();
  const { onShowToast } = useToast();
  const chartRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLDivElement>(null);
  const context: TContext = useContext(UserContext);
  const [customerOptions, setCustomerOptions] = useState<TDropdownOptions[]>(
    []
  );
  const [fileOptions, setFileOptions] = useState<TDropdownOptions[]>([]);
  const [data, setData] = useState<TFileManagerMaster[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [selectedFileIds, setSelectedFileIds] = useState<number[] | null>([0]);
  const [chartData, setChartData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [fileLoader, setFileLoader] = useState(false);
  const [fromDate, setFromDate] = useState(dayjs().subtract(7, "day"));
  const [toDate, setToDate] = useState(dayjs());
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [filterMode, setFilterMode] = useState<"time" | "file">("file");
  const [selectedDateType, setSelectedDateType] = useState<string>("last7days");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const shouldKeepDropdownOpenRef = useRef(false);

  const colorPalette = [
    "#d8811dff",
    "#9d7695ff",
    "#447594ff",
    "#CCCCCC",
    "#6c7869",
    "#dfc6af",
    "#982424ff",
    "#37447cff",
    "#e39e83",
    "#3d7475",
    "#8FBF59",
  ];

  const handleDownloadPDF = async () => {
    if (!chartRef.current) {
      console.error("Chart element not found");
      return;
    }

    try {
      setIsPdfGenerating(true);
      const doc = new jsPDF("p", "mm", "a4");
      const width = doc.internal.pageSize.getWidth();
      const height = doc.internal.pageSize.getHeight();

      // Load Fonts
      const loadFont = async (path: string, name: string, style: string) => {
        const response = await fetch(path);
        const blob = await response.blob();
        const reader = new FileReader();
        return new Promise<void>((resolve) => {
          reader.onloadend = () => {
            const base64data = reader.result as string;
            const base64 = base64data.split(",")[1];
            doc.addFileToVFS(`${name}-${style}.ttf`, base64);
            doc.addFont(`${name}-${style}.ttf`, name, style);
            resolve();
          };
          reader.readAsDataURL(blob);
        });
      };

      await Promise.all([
        loadFont(
          "/assets/fonts/Montserrat-Regular.ttf",
          "Montserrat",
          "normal"
        ),
        loadFont("/assets/fonts/Montserrat-Bold.ttf", "Montserrat", "bold"),
      ]);

      doc.setFont("Montserrat", "normal");

      // --- Header ---
      // Add Logo
      try {
        const logoResponse = await fetch("/assets/images/logo.png");
        if (logoResponse.ok) {
          const logoBlob = await logoResponse.blob();
          const logoBase64 = await new Promise<string>((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.readAsDataURL(logoBlob);
          });
          doc.addImage(logoBase64, "PNG", 14, 10, 30, 10); // Adjust size/pos as needed
        }
      } catch (e) {
        console.warn("Logo not found", e);
      }

      doc.setFontSize(18);
      doc.setFont("Montserrat", "bold");
      doc.setTextColor(41, 128, 185);
      doc.text("Dashboard Report", width - 14, 18, { align: "right" });

      doc.setFontSize(10);
      doc.setFont("Montserrat", "normal");
      doc.setTextColor(100);
      doc.text(
        `Generated on: ${dayjs().format("DD MMM YYYY, HH:mm")}`,
        width - 14,
        24,
        { align: "right" }
      );

      doc.setDrawColor(200);
      doc.line(14, 30, width - 14, 30);

      let currentY = 40;

      // --- Chart Section ---
      doc.setFontSize(14);
      doc.setFont("Montserrat", "bold");
      doc.setTextColor(41, 128, 185);
      doc.text("Product Status Overview", 14, currentY);
      currentY += 10;

      const canvas = await html2canvas(chartRef.current, { scale: 2 });
      const imgData = canvas.toDataURL("image/png");
      const chartHeight = (canvas.height * (width - 28)) / canvas.width;

      // Check if chart fits, else add page
      if (currentY + chartHeight > height - 20) {
        doc.addPage();
        currentY = 20;
      }

      doc.addImage(imgData, "PNG", 14, currentY, width - 28, chartHeight);
      currentY += chartHeight + 10;

      // Chart Data Table
      const chartTableHead = [["Stage Name", "Product Name", "Count"]];
      const chartTableBody: any[] = [];
      if (chartData?.labels && chartData?.datasets) {
        chartData.labels.forEach((stage: string, i: number) => {
          chartData.datasets.forEach((dataset: any) => {
            const count = dataset.data[i];
            if (count > 0) {
              chartTableBody.push([stage, dataset.label, count]);
            }
          });
        });
      }

      autoTable(doc, {
        startY: currentY,
        head: chartTableHead,
        body: chartTableBody,
        theme: "grid",
        styles: {
          font: "Montserrat",
          fontSize: 9,
          cellPadding: 3,
        },
        headStyles: {
          fillColor: [41, 128, 185],
          fontStyle: "bold",
          halign: "center",
        },
        columnStyles: {
          2: { halign: "center" },
        },
        margin: { top: 20 },
      });

      currentY = (doc as any).lastAutoTable?.finalY || currentY;
      currentY += 20;

      // --- Recent Uploads Section ---
      // Check for page break
      if (currentY + 30 > height - 20) {
        doc.addPage();
        currentY = 20;
      }

      doc.setFontSize(14);
      doc.setFont("Montserrat", "bold");
      doc.setTextColor(41, 128, 185);
      doc.text("File Management - Recent Uploads", 14, currentY);
      currentY += 10;

      const uploadsHead = [["File Name", "Uploaded By", "Date", "Records"]];
      const uploadsBody = (data || []).map((d: any) => [
        d.uploadFileName || "",
        d.createdByName || "",
        DateUtils.format(d.createdOn, context?.user?.dateFormat, true) || "",
        (d.recordCount ?? "") + "",
      ]);

      autoTable(doc, {
        startY: currentY,
        head: uploadsHead,
        body: uploadsBody,
        theme: "grid",
        styles: {
          font: "Montserrat",
          fontSize: 9,
          cellPadding: 3,
        },
        headStyles: {
          fillColor: [41, 128, 185],
          fontStyle: "bold",
          halign: "center",
        },
        columnStyles: {
          3: { halign: "center" },
        },
        margin: { top: 20 },
      });

      // --- Footer (Page Numbers) ---
      const pageCount = (doc as any).internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setFont("Montserrat", "normal");
        doc.setTextColor(150);
        doc.text(`Page ${i} of ${pageCount}`, width / 2, height - 10, {
          align: "center",
        });
      }

      doc.save("dashboard.pdf");
    } catch (err) {
      console.error(err);
      onShowToast({
        type: ToastType.error,
        title: <CloseCircleFilled />,
        position: ToastOpen.leftBottom,
        content: "Failed to generate PDF",
      });
    } finally {
      setIsPdfGenerating(false);
    }
  };

  const generateRandomMatteColor = () => {
    const hue = Math.floor(Math.random() * 360);
    const saturation = Math.floor(Math.random() * 25) + 35;
    const lightness = Math.floor(Math.random() * 30) + 40;

    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
  };

  const fetchCustomerOptions = async () => {
    try {
      setIsLoading(true);
      const response: any = await post(GetCustomerList, {
        data: { currentPage: 1, pageSize: -1 },
      });

      if (response?.dataResponse?.returnCode === eResultCode.SUCCESS) {
        const mapped = Utils.mapDropdownOptions(response.data);
        setCustomerOptions(mapped);

        if (mapped.length > 0) {
          const firstId = mapped[0].value;
          setSelectedId(firstId);
        }
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseCircleFilled />,
          position: ToastOpen.leftBottom,
          content: response?.dataResponse?.description || "Failed to load list",
        });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCustomerUploadMaster = async (customerId: number) => {
    try {
      setFileLoader(true);
      const response: any = await post(GetCustomerUploadMasterList, {
        data: {
          currentPage: 1,
          pageSize: -1,
          customerId,
          isDashboardView: true,
        },
      });

      if (response?.dataResponse?.returnCode === eResultCode.SUCCESS) {
        const mapped = Utils.mapDropdownOptions(
          response.data,
          "id",
          "uploadFileName"
        );
        mapped.length > 1 && mapped.push({ label: "All", value: 0 });
        const defaultIds = mapped.length ? [mapped?.[0]?.value] : null;
        setSelectedFileIds(defaultIds);
        setData(response?.data);
        setFileOptions(mapped);
        fetchProductStatus(customerId, defaultIds);
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseCircleFilled />,
          position: ToastOpen.leftBottom,
          content: response?.dataResponse?.description || "Failed to load list",
        });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setFileLoader(false);
    }
  };

  const fetchProductStatus = async (
    customerId: number,
    customFileIds?: number[] | null
  ) => {
    try {
      setIsLoading(true);
      const payload = {
        Data: {
          customerId,
          fromDate:
            filterMode === "time" ? fromDate.format("DD-MMM-YYYY") : null,
          toDate: filterMode === "time" ? toDate.format("DD-MMM-YYYY") : null,
          customerUploadMasterIds:
            filterMode === "file"
              ? customFileIds !== undefined
                ? customFileIds
                : selectedFileIds || [0]
              : null,
        },
      };
      const response: any = await post(GetProductStatusCount, payload);

      if (response?.dataResponse?.returnCode === eResultCode.SUCCESS) {
        const stages = response.data.stage || [];

        const allProducts = new Set<string>();
        stages.forEach((s: any) =>
          s.product.forEach((p: any) => allProducts.add(p.airtechModel))
        );
        const labels = stages.map((s: any) => s.stageName);
        const productNames = Array.from(allProducts);

        const datasets = productNames.map((airtechModel, idx) => ({
          label: airtechModel,
          data: stages.map((stage: any) => {
            const matchingProducts = stage.product.filter(
              (p: any) => p.airtechModel === airtechModel
            );
            const totalCount = matchingProducts.reduce(
              (sum: number, p: any) => sum + (p.count || 0),
              0
            );
            return totalCount;
          }),
          backgroundColor: colorPalette[idx]
            ? colorPalette[idx]
            : generateRandomMatteColor(),

          borderWidth: 1,
          barThickness: 40,
        }));

        setChartData({ labels, datasets });
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseCircleFilled />,
          position: ToastOpen.leftBottom,
          content: response?.dataResponse?.description || "Failed to load data",
        });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomerOptions();
  }, []);

  useEffect(() => {
    if (selectedId) {
      fetchCustomerUploadMaster(selectedId);
    }
  }, [selectedId]);

  useEffect(() => {
    if (selectedId) {
      fetchProductStatus(selectedId);
    }
  }, [fromDate, toDate, filterMode]);

  const handleFileChange = (values: number[]) => {
    if (values.includes(0)) {
      if (selectedFileIds?.includes(0)) {
        const newValues = values.filter((v) => v !== 0);
        setSelectedFileIds(newValues);
      } else {
        setSelectedFileIds([0]);
      }
    } else {
      if (values.length === 0) {
        setSelectedFileIds([0]);
      } else {
        setSelectedFileIds(values);
      }
    }
  };

  const handleDateRangeChange = (e: any) => {
    const { value } = e.target;
    setSelectedDateType(value);
    switch (value) {
      case "today":
        setFromDate(dayjs());
        setToDate(dayjs());
        break;
      case "last7days":
        setFromDate(dayjs().subtract(7, "day"));
        setToDate(dayjs());
        break;
      case "currentMonth":
        setFromDate(dayjs().startOf("month"));
        setToDate(dayjs());
        break;
      case "custom":
        break;
      default:
        break;
    }
  };

  const getDurationString = () => {
    if (filterMode === "file") {
      if (selectedFileIds?.includes(0)) return "File: All";
      if (selectedFileIds?.length === 1) {
        const file = fileOptions.find((f) => f.value === selectedFileIds[0]);
        return file ? `File: ${file.label}` : "";
      }
      return `File: ${selectedFileIds?.length} Selected`;
    } else {
      const fromStr = fromDate.format("DD-MMM-YYYY");
      const toStr = toDate.format("DD-MMM-YYYY");
      switch (selectedDateType) {
        case "today":
          return "Today";
        case "last7days":
          return `In Last 7 Days (${fromStr} to ${toStr})`;
        case "currentMonth":
          return `Current Month (${fromDate.format("MMMM")})`;
        case "custom":
          return `Custom Date Range (${fromStr} to ${toStr})`;
        default:
          return "";
      }
    }
  };

  return (
    <Layout className={styles.dashboardLayout}>
      <Form layout="vertical">
        <Row gutter={16} className={styles.filterRow}>
          <Col xs={24} sm={12} md={4} lg={4}>
            <Form.Item label="Customer">
              <Select
                disabled={isLoading || isPdfGenerating}
                placeholder="Select Customer"
                options={customerOptions}
                value={selectedId ?? undefined}
                onChange={(value: number) => {
                  setSelectedId(value);
                }}
                showSearch
                filterOption={(input, option: any) =>
                  option?.label?.toLowerCase().includes(input.toLowerCase())
                }
                style={{ width: "100%" }}
                size="middle"
              />
            </Form.Item>
          </Col>

          {/* <Col
            xs={24}
            sm={12}
            md={6}
            lg={6}
            style={{ display: "flex", alignItems: "center" }}
          >
            <div>
              <Segmented
                options={[
                  { label: "Duration", value: "time" },
                  { label: "File", value: "file" },
                ]}
                value={filterMode}
                onChange={(value) => {
                  setFilterMode(value as "time" | "file");
                  if (value === "time") {
                    setSelectedFileIds([0]);
                  }
                }}
              />
            </div>
          </Col> */}

          {/* time wise filter */}
          {/* {filterMode === "time" && (
            <Col
              xs={24}
              sm={24}
              md={10}
              lg={10}
              className={styles.dateRangeCol}
            >
              <Radio.Group
                onChange={handleDateRangeChange}
                value={selectedDateType}
                optionType="button"
              >
                <Radio.Button value="today">Today</Radio.Button>
                <Radio.Button value="last7days">Last 7 Days</Radio.Button>
                <Radio.Button value="currentMonth">Current Month</Radio.Button>
                <Radio.Button
                  value="custom"
                  onClick={() => setIsModalVisible(true)}
                >
                  Custom
                </Radio.Button>
              </Radio.Group>
            </Col>
          )} */}

          {/* file wise filter */}
          {filterMode === "file" && (
            <Col xs={24} sm={24} md={10} lg={10}>
              <Form.Item label="File">
                <Select
                  mode="multiple"
                  maxTagCount="responsive"
                  disabled={isLoading || isPdfGenerating}
                  placeholder="Select File"
                  options={fileOptions}
                  value={selectedFileIds}
                  onChange={handleFileChange}
                  open={dropdownOpen}
                  onDropdownVisibleChange={(visible) => {
                    if (!visible && shouldKeepDropdownOpenRef.current) {
                      shouldKeepDropdownOpenRef.current = false;
                      return;
                    }
                    setDropdownOpen(visible);
                    if (!visible && selectedId) {
                      fetchProductStatus(selectedId);
                    }
                  }}
                  onSelect={() => {
                    shouldKeepDropdownOpenRef.current = true;
                  }}
                  onDeselect={() => {
                    shouldKeepDropdownOpenRef.current = true;
                  }}
                  autoClearSearchValue={false}
                  showSearch
                  filterOption={(input, option: any) =>
                    option?.label?.toLowerCase().includes(input.toLowerCase())
                  }
                  style={{ width: "100%" }}
                  size="middle"
                />
              </Form.Item>
            </Col>
          )}
          {
            <Col
              xs={24}
              sm={12}
              md={4}
              lg={4}
              style={{
                display: "flex",
                alignItems: "flex-end",
              }}
            >
              <Button
                type="primary"
                // size="small"
                onClick={handleDownloadPDF}
                loading={isPdfGenerating}
                disabled={isLoading}
                icon={<DownloadOutlined></DownloadOutlined>}
              >
                Download
              </Button>
            </Col>
          }
        </Row>
      </Form>
      <div className={styles.chartListWrapper}>
        <div className={styles.chartWrapper}>
          <div ref={chartRef}>
            <StackedBarChart
              chartData={chartData}
              selectedId={selectedId}
              fetchProductStatus={fetchProductStatus}
              isLoading={isLoading}
              duration={getDurationString()}
            />
          </div>
        </div>

        <div className={styles.listWrapper}>
          <div ref={tableRef}>
            <RecentUploadsList
              data={data}
              context={context}
              loading={fileLoader}
            />
          </div>
        </div>
      </div>

      <Modal
        title="Select Custom Date Range"
        visible={isModalVisible}
        onOk={() => setIsModalVisible(false)}
        onCancel={() => setIsModalVisible(false)}
        style={{ padding: "20px" }}
      >
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item label="From Date">
              <DatePicker
                format={context?.user?.dateFormat?.toUpperCase()}
                value={fromDate}
                onChange={(date) => setFromDate(date)}
                placeholder="Select Date"
                style={{ width: "100%" }}
                size="small"
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item label="To Date">
              <DatePicker
                maxDate={dayjs()}
                format={context?.user?.dateFormat?.toUpperCase()}
                value={toDate}
                onChange={(date) => setToDate(date)}
                placeholder="Select Date"
                style={{ width: "100%" }}
                size="small"
              />
            </Form.Item>
          </Col>
        </Row>
      </Modal>
    </Layout>
  );
}

export default DashboardView;
